import * as React from 'react';
import { cn } from '../lib/utils';

export interface CompassLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showWordmark?: boolean;
  animated?: boolean;
  subtitle?: string;
  className?: string;
  /** Surcharge la couleur du wordmark « JeRaisonne » (ex. `text-white` sur fond sombre). */
  wordmarkClassName?: string;
  /** Diamètre du disque en pixels, à la place de celui de `size` (aiguille comprise). */
  diameter?: number;
}

const SIZE_MAP = {
  sm: { px: 28, textSize: 'text-sm', subSize: 'text-[10px]' },
  md: { px: 36, textSize: 'text-[15px]', subSize: 'text-[11px]' },
  lg: { px: 52, textSize: 'text-xl', subSize: 'text-xs' },
  xl: { px: 88, textSize: 'text-4xl', subSize: 'text-sm' },
  '2xl': { px: 128, textSize: 'text-5xl sm:text-6xl', subSize: 'text-base' },
} as const;

// Realistic damped-oscillation compass spin: 2 s action, 10 s stable → 12 s loop
const ANIMATION_CSS = `
@keyframes jr-compass-spin {
  0%    { transform: rotate(0deg);   animation-timing-function: cubic-bezier(0.55,0,0.35,1); }
  7%    { transform: rotate(188deg); animation-timing-function: cubic-bezier(0.4,0,0.5,1);  }
  11%   { transform: rotate(146deg); animation-timing-function: cubic-bezier(0.4,0,0.6,1);  }
  14%   { transform: rotate(172deg); animation-timing-function: cubic-bezier(0.4,0,0.6,1);  }
  15.5% { transform: rotate(158deg); animation-timing-function: ease;                       }
  16.5% { transform: rotate(164deg); animation-timing-function: ease;                       }
  17%   { transform: rotate(162deg);                                                         }
  100%  { transform: rotate(162deg);                                                         }
}
.jr-cmp-needle {
  transform-box: fill-box;
  transform-origin: center;
}
.jr-cmp-needle--anim {
  animation: jr-compass-spin 12s linear infinite;
}
`;

export function CompassLogo({
  size = 'md',
  showWordmark = true,
  animated = true,
  subtitle,
  className,
  wordmarkClassName,
  diameter,
}: CompassLogoProps) {
  /*
   * ⚠️ L'id du dégradé est UNIQUE par logo : deux logos sur la même page (la connexion en a un
   * compact et un grand) partageaient le même id, et le disque prenait celui du premier — qui est
   * dans une colonne `display:none` en mobile, donc pas de fond du tout.
   */
  const uid = React.useId().replace(/:/g, '');
  const bgId = `jr-cmpbg-${uid}`;
  const { px: sizePx, textSize, subSize } = SIZE_MAP[size];
  const px = diameter ?? sizePx;

  return (
    <div className={cn('inline-flex items-center gap-2.5', className)}>
      {/* React 19 deduplicates <style> with same href across renders */}
      <style href="jr-compass-anim" precedence="default">
        {ANIMATION_CSS}
      </style>
      <div
        style={{
          width: `${px}px`,
          height: `${px}px`,
        }}
        className="relative"
      >
        <svg
          width={px}
          height={px}
          viewBox="0 0 1024 1024"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <rect width="1024" height="1024" rx="512" fill={`url(#${bgId})`} />
          <path
            d="M512 105.5C737.126 105.5 919.5 287.566 919.5 512C919.5 736.434 737.126 918.5 512 918.5C286.874 918.5 104.5 736.434 104.5 512C104.5 287.566 286.874 105.5 512 105.5Z"
            stroke="white"
            strokeWidth="65"
          />
          <rect x="496" y="73" width="32" height="107" rx="16" fill="white" />
          <rect x="496" y="844" width="32" height="107" rx="16" fill="white" />
          <rect x="845" y="496" width="107" height="32" rx="16" fill="white" />
          <rect x="72" y="496" width="107" height="32" rx="16" fill="white" />
          <defs>
            <linearGradient
              id={bgId}
              x1="104"
              y1="162"
              x2="894"
              y2="779"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#04A0FE" />
              <stop offset="1" stopColor="#0566F2" />
            </linearGradient>
          </defs>
        </svg>
        {/* Needle — this group rotates during animation */}
        <svg
          width={Math.floor((456 * px) / 1024)}
          height={Math.floor((461 * px) / 1024)}
          viewBox="0 0 456 461"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cn(
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
            'jr-cmp-needle',
            animated && 'jr-cmp-needle--anim',
          )}
        >
          <path
            d="M421.561 16.1416C433.35 10.6159 445.638 22.767 440.245 34.6175L313.043 314.109C312.048 316.296 310.298 318.052 308.114 319.055L36.7886 443.611C24.1399 449.418 11.0897 436.364 16.9004 423.717L142.36 150.657C143.742 147.65 146.144 145.229 149.14 143.825L421.561 16.1416Z"
            fill="white"
          />
          <ellipse cx="228" cy="230.5" rx="56.5" ry="55" fill="#0581F7" />
        </svg>
      </div>
      {showWordmark && (
        <div className="flex flex-col justify-center">
          <p
            className={cn(
              'font-display text-primary-500 font-extrabold leading-none',
              textSize,
              wordmarkClassName,
            )}
          >
            JeRaisonne
          </p>
          {subtitle && <p className={cn('text-ink-500 mt-0.5 font-sans', subSize)}>{subtitle}</p>}
        </div>
      )}
    </div>
  );
}
